import "bootstrap";
import { _ } from "../polyglot/polyglot.js";
import { widget } from "@foretagsplatsen/widgetjs";
import jQuery from "jquery";

/**
 * Generic modal dialog based on bootstrap markup.
 *
 * @param {Renderable} [spec.header] - Title of the dialog, `'Dialog'` if not
 * provided.
 * @param {Renderable} [spec.body] - Content of the dialog, renders nothing
 * by default.
 * @param {footer} [spec.footer] - Footer of the dialog, visible unless
 * `showFooter` is false.
 * @param {boolean} [spec.showFooter] - When `true`, render the
 * footer. `true` by default.
 * @param {string} [spec.cssClass] - Extra CSS class attached to the root
 * div of the dialog.
 * @param {boolean} [spec.wide=false] - Flag for showing a wide modal.
 */
export default widget.subclass((that, my) => {
	my.initialize = function (spec) {
		my.super(spec);
		my.title = spec.header || _("Dialog");
		my.content = spec.body || "";
		my.footer = spec.footer || "";
		my.showFooter = spec.showFooter === undefined ? true : spec.showFooter;
		my.cssClass = spec.cssClass || "";
		my.visible = false;
		my.wide = spec.wide === undefined ? false : spec.wide;
	};

	// The dialog is first attached hidden in the DOM (usually on body).
	// Then the event is triggered but it is not consistent with the other
	// widgets (where the event is triggered when the widget is displayed).
	//
	// The current solution is to keep the super implementation on `my` and
	// nullify the dialog events. Then when the dialog is shown, the events
	// are fired by hand.
	my.triggerDidAttach = that.triggerDidAttach;
	my.triggerWillAttach = that.triggerWillAttach;

	that.triggerDidAttach = function () {};
	that.triggerWillAttach = function () {};

	that.hide = function () {
		const div = that.asJQuery().find(".modal");
		div.modal("hide");
		unbindEscape();
		my.visible = false;
	};

	that.show = function () {
		my.show();
		my.visible = true;
	};

	that.update = function () {
		const visible = my.visible;

		if (visible) {
			that.hide();
		}

		that.super(...arguments);

		if (visible) {
			that.show();
		}
	};

	that.renderContentOn = function (html) {
		html.div(
			{
				klass: "modal",
				"data-test": "dialog",
			},
			html.div(
				{ klass: ["modal-dialog", { wide: my.wide }] },
				html.div(
					{ klass: ["modal-content", my.cssClass] },
					my.renderHeaderOn,
					my.renderBodyOn,
					my.footerRenderOn,
				),
			),
		);
	};

	my.renderHeaderOn = function (html) {
		html.div(
			{ klass: "modal-header" },
			html.a({ klass: "close" }, "×").click(that.hide),
			html.h3(my.title),
		);
	};

	my.renderBodyOn = function (html) {
		html.div({ klass: "modal-body" }, my.content);
	};

	my.footerRenderOn = function (html) {
		if (!my.showFooter) {
			return;
		}

		html.div({ klass: "modal-footer" }, (html) => {
			if (my.footer) {
				html.render(my.footer);
			} else {
				html.button({ klass: "btn btn-primary" }, _("Close")).click(
					that.hide,
				);
			}
		});
	};

	my.show = function () {
		that.appendTo("body");
		const modal = that.asJQuery().find(".modal");
		my.registerEvents(modal);
		modal.modal("show");
	};

	my.registerEvents = function (modal) {
		modal.on("show.bs.modal", () => {
			my.triggerWillAttach();
		});

		modal.on("shown.bs.modal", () => {
			my.triggerDidAttach();
		});

		modal.on("hidden.bs.modal", () => {
			that.asJQuery().remove();
		});

		modal.modal({ backdrop: true });

		bindEscape();
	};

	/**
	 * Append the dialog to the DOM and show it using a CSS
	 * transition if available, and a 'backdrop' overlay.
	 *
	 * Remove the dialog widget from the DOM when closing it
	 */
	function bindEscape() {
		jQuery(document).on("keyup.bindEscape", (event) => {
			if (event.keyCode !== 27) {
				return;
			}
			that.hide();
		});
	}

	function unbindEscape() {
		jQuery(document).unbind("keyup.bindEscape");
	}
});
